import {
  DraftEntryResponseOption,
  DraftMatrixRow,
  KpiMetricType
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi,
  EntryType,
  QuestionSettingCode,
  QuestionTypeCode
} from '../../../data/model/questionnaire'
import {
  checkIfMatrixMultipleChoice,
  checkIfMultipleChoice,
  getQuestionSettings
} from '../../../utils/questionnaireUtils'
import {
  isTempMatrixRowLk,
  isTempResponseOptionLk
} from '../Questionnaire.utils'

export type KpiSubjectKind =
  | 'singleSelect'
  | 'multiSelect'
  | 'ranked'
  | 'slider'
  | 'matrixSingle'
  | 'matrixMulti'

// Mirrors what the server accepts: only an explicit multiple_choice setting is multi.
const ALLOWED_METRICS: Record<KpiSubjectKind, KpiMetricType[]> = {
  singleSelect: [KpiMetricType.Share, KpiMetricType.Net],
  multiSelect: [KpiMetricType.Share],
  ranked: [KpiMetricType.RankedTop],
  slider: [KpiMetricType.Average],
  matrixSingle: [KpiMetricType.Share, KpiMetricType.Net],
  matrixMulti: [KpiMetricType.Share]
}

export const getKpiSubjectKind = (
  entry: DraftQuestionnaireEntry
): KpiSubjectKind | null => {
  if (entry.entryType === EntryType.MatrixEntryType) {
    return checkIfMatrixMultipleChoice(entry.entryItem.settingValues)
      ? 'matrixMulti'
      : 'matrixSingle'
  }
  if (entry.entryType !== EntryType.QuestionEntryType) {
    return null
  }
  const { settingValues } = entry.entryItem
  switch (entry.entryItem.questionTypeCode) {
    case QuestionTypeCode.Basic:
      // Constant sum (auto-sum) answers are numbers adding up to a total, which no KPI measures yet.
      if (
        getQuestionSettings(settingValues).has(
          QuestionSettingCode.AutoSumTarget
        )
      ) {
        return null
      }
      return checkIfMultipleChoice(settingValues)
        ? 'multiSelect'
        : 'singleSelect'
    case QuestionTypeCode.Ranked:
      return 'ranked'
    case QuestionTypeCode.Scale:
      return 'slider'
    default:
      return null
  }
}

export const getAllowedKpiMetrics = (kind: KpiSubjectKind) =>
  ALLOWED_METRICS[kind]

export const isMatrixKpiSubject = (kind: KpiSubjectKind) =>
  kind === 'matrixSingle' || kind === 'matrixMulti'

export const isMultipleChoiceKpiSubject = (kind: KpiSubjectKind) =>
  kind === 'multiSelect' || kind === 'matrixMulti'

/** The metric a KPI marked from an option's menu gets, or null where options can't be marked. */
export const getOptionKpiMetric = (
  kind: KpiSubjectKind | null
): KpiMetricType | null => {
  if (kind === 'singleSelect' || kind === 'multiSelect') {
    return KpiMetricType.Share
  }
  return kind === 'ranked' ? KpiMetricType.RankedTop : null
}

/** Marking an option stores a one-option KPI, so that is what "this option's KPI" means. */
export const findOptionKpi = (
  kpis: EntryKpi[],
  optionId: string,
  metricType: KpiMetricType
): EntryKpi | undefined =>
  kpis.find(
    (kpi) =>
      !kpi.rowId &&
      kpi.metricType === metricType &&
      kpi.optionIds.length === 1 &&
      kpi.optionIds.includes(optionId) &&
      !kpi.negativeOptionIds?.length
  )

export const getKpiName = (
  kpi: Pick<EntryKpi, 'label'>,
  index: number,
  count: number
): string => {
  const label = kpi.label?.trim()
  if (label) {
    return label
  }
  return count > 1 ? `KPI ${index + 1}` : 'KPI'
}

const byPosition = <T extends { position: number }>(a: T, b: T) =>
  a.position - b.position

/** Saved options (matrix: columns) in display order; unsaved ones can't be ticked yet. */
export const getKpiOptions = (
  entry: DraftQuestionnaireEntry
): DraftEntryResponseOption[] => {
  if (
    entry.entryType !== EntryType.QuestionEntryType &&
    entry.entryType !== EntryType.MatrixEntryType
  ) {
    return []
  }
  return entry.entryItem.responseOptions
    .filter(
      (option) =>
        option.createdDate !== '' &&
        !isTempResponseOptionLk(option.responseOptionLk)
    )
    .sort(byPosition)
}

/** A KPI follows its content from wave to wave by Tracking ID, so content without one can't be counted. */
export const hasTrackingId = (item: { trackingId?: string | null }) =>
  !!item.trackingId

export const getTrackedKpiOptions = (entry: DraftQuestionnaireEntry) =>
  getKpiOptions(entry).filter(hasTrackingId)

export const getKpiMatrixRows = (
  entry: DraftQuestionnaireEntry
): DraftMatrixRow[] =>
  entry.entryType === EntryType.MatrixEntryType
    ? entry.entryItem.matrixRows
        .filter(
          (row) => row.createdDate !== '' && !isTempMatrixRowLk(row.questionLk)
        )
        .sort(byPosition)
    : []

export const getKpiOptionLabel = (option: DraftEntryResponseOption) =>
  option.responseOption?.value.trim() || `Option ${option.position + 1}`

export const getKpiRowLabel = (row: DraftMatrixRow) =>
  row.question?.text.trim() || `Row ${row.position + 1}`
